import { useMemo, useCallback, useState } from "react";
import { Field } from "src/search/types";
import { AggregationConfig } from "../types";

export interface PivotTableConfig {
  rowGroups: Field[]; // Groups to show as rows
  columnGroups: Field[]; // Groups to pivot as columns
  aggregations: AggregationConfig[];
  columnInfo: Array<{
    type: string;
    key: string;
    target: string;
    attribute: string;
    unit?: string;
    reducer?: string;
    diffMethod?: string;
  }>;
  measuresAsRows: boolean;
  rollupMeasureKey?: string;
}

export interface PivotRow {
  key: string;
  value: any;
  children?: PivotRow[];
  measures: Record<string, any>;
  depth: number;
  isExpandable: boolean;
  groupBys?: Field[];
  measureName?: string;
  rollupDetails?: Record<string, any>;
  field?: Field;
}

export interface UsePivotTableResult {
  processedData: PivotRow[];
  expandedRows: Set<string>;
  toggleExpanded: (rowKey: string) => void;
  columnKeys: string[];
}

function getDisplayValue(value: any): string {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object") {
    return value.name || value.value || value.uuid || String(value);
  }
  return String(value);
}

function generateColumnKeys(data: any[], columnGroups: Field[]): string[] {
  const uniqueKeys = new Set<string>();

  data.forEach((row) => {
    const key = columnGroups
      .map((group) => {
        const value = row[`${group.target}.${group.attribute}`];
        return getDisplayValue(value);
      })
      .join("|");
    if (key) uniqueKeys.add(key);
  });

  return [...uniqueKeys].sort();
}

function filterByColumnKey(
  data: any[],
  columnKey: string,
  columnGroups: Field[]
): any[] {
  const columnValues = columnKey.split("|");
  return data.filter((row) => {
    return columnGroups.every((group, index) => {
      const value = row[`${group.target}.${group.attribute}`];
      return getDisplayValue(value) === columnValues[index];
    });
  });
}

// Aggregation functions registry
const aggregators = {
  sum: (values: number[]) => values.reduce((a, b) => a + b, 0),
  count: (values: number[]) => values.length,
  avg: (values: number[]) =>
    values.length > 0
      ? values.reduce((a, b) => a + b, 0) / values.length
      : null,
  min: (values: number[]) => (values.length > 0 ? Math.min(...values) : null),
  max: (values: number[]) => (values.length > 0 ? Math.max(...values) : null),
} as const;

type AggregatorType = keyof typeof aggregators;

interface ComparisonValue {
  current: number | null;
  comparison: number | null;
  delta: number | null;
  percentage: number | null;
  raw: string;
}

function parseComparisonValue(value: any): ComparisonValue | null {
  // If not a comparison value, return the value as is
  if (typeof value !== "string" || !value.includes(",")) {
    return value;
  }

  const [current, comparison] = value.split(",").map((v) => {
    if (v === "__VZBL::null__" || v === "null") return null;
    const num = Number(v);
    return Number.isNaN(num) ? null : num;
  });

  // Calculate delta
  const delta =
    comparison !== null && current !== null ? comparison - current : null;

  // Calculate percentage change
  let percentage: number | null = null;
  if (comparison !== null && current !== null) {
    if (current === 0) {
      percentage = comparison > 0 ? 100 : comparison < 0 ? -100 : 0;
    } else {
      percentage = ((comparison - current) / Math.abs(current)) * 100;
    }
  } else if (current === null && comparison !== null) {
    percentage = 100; // null to value = 100% increase
  } else if (current !== null && comparison === null) {
    percentage = -100; // value to null = 100% decrease
  }

  return {
    current,
    comparison,
    delta,
    percentage,
    raw: value,
  };
}

function aggregateMeasures(
  data: any[],
  aggregations: AggregationConfig[]
): Record<string, ComparisonValue | null> {
  const measures: Record<string, ComparisonValue | null> = {};

  aggregations.forEach((agg) => {
    const aggregationField = agg.formula
      ? agg.field
      : `${agg.reducer}(${agg.field})`;

    // Get all values including comparison values
    const values = data
      .map((row) => {
        const rawValue = row[aggregationField];
        return parseComparisonValue(rawValue);
      })
      .filter((v) => v !== null);

    if (values.length === 0) {
      measures[agg.key] = null;
      return;
    }

    measures[agg.key] = values[0];
  });

  return measures;
}

interface RollupSummary {
  displayValue: ComparisonValue | null;
  details: {
    range: string;
    coverage: string;
    trend?: string;
    stats: {
      avg?: number;
      min?: number;
      max?: number;
      sum?: number;
      count?: number;
      up?: number;
      down?: number;
      same?: number;
      noData?: number;
      total?: number;
      measures: {
        up: string[];
        down: string[];
        same: string[];
        noData: string[];
      };
    };
  };
}

const pivotAggregators = {
  getRangeSummary: (
    rows: PivotRow[],
    columnKey: string,
    measure: AggregationConfig,
    allColumnKeys: string[]
  ): RollupSummary => {
    // Find the previous column from the columnKeys array
    const columnIndex = allColumnKeys.indexOf(columnKey);
    if (columnIndex <= 0) {
      // First column or invalid column - no comparison possible
      return {
        displayValue: {
          current: rows.length,
          comparison: null,
          delta: null,
          percentage: null,
          raw: rows.length.toString(),
        },
        details: {
          range: "First column",
          coverage: `${rows.length} measures`,
          trend: "→",
          stats: {
            total: rows.length,
            noData: 0,
            measures: {
              up: [],
              down: [],
              same: [],
              noData: [],
            },
          },
        },
      };
    }

    const prevColumnKey = allColumnKeys[columnIndex - 1];

    // Count trends by comparing with previous column
    let upCount = 0;
    let downCount = 0;
    let sameCount = 0;
    let noDataCount = 0;

    // Track measures in each category
    const measures = {
      up: [] as string[],
      down: [] as string[],
      same: [] as string[],
      noData: [] as string[],
    };

    rows.forEach((row) => {
      if (!row.measureName) return;

      const currentValue = row.measures[columnKey];
      const previousValue = row.measures[prevColumnKey];

      if (
        currentValue === null ||
        currentValue === undefined ||
        previousValue === null ||
        previousValue === undefined
      ) {
        noDataCount++;
        measures.noData.push(row.measureName);
      } else if (currentValue > previousValue) {
        upCount++;
        measures.up.push(row.measureName);
      } else if (currentValue < previousValue) {
        downCount++;
        measures.down.push(row.measureName);
      } else {
        sameCount++;
        measures.same.push(row.measureName);
      }
    });

    const totalMeasures = rows.length;
    const trend = upCount > downCount ? "↑" : downCount > upCount ? "↓" : "→";

    return {
      displayValue: {
        current: totalMeasures,
        comparison: null,
        delta: upCount - downCount,
        percentage:
          totalMeasures > 0
            ? ((upCount - downCount) / totalMeasures) * 100
            : null,
        raw: totalMeasures.toString(),
      },
      details: {
        range: `${upCount}↑ ${downCount}↓ ${sameCount}→`,
        coverage: `${totalMeasures - noDataCount}/${totalMeasures} measures`,
        trend,
        stats: {
          up: upCount,
          down: downCount,
          same: sameCount,
          noData: noDataCount,
          total: totalMeasures,
          measures,
        },
      },
    };
  },
};

function computeRollupDetails(
  children: PivotRow[],
  measureKeys: string[],
  rollupMeasureKey?: string
): Record<string, Record<string, any>> | undefined {
  const rollupDetails: Record<string, Record<string, any>> = {};
  if (children.length === 0) return rollupDetails;
  measureKeys.forEach((key) => {
    const values = children.flatMap((child) => {
      if ((child.children?.length ?? 0) > 0) {
        return child.rollupDetails?.[key]?.values || [];
      }

      // if rollupMeasureKey is set, and the child is not the rollup measure, skip
      if (rollupMeasureKey && child.field?.measureKey !== rollupMeasureKey) {
        return [];
      }

      const measureValue = child.measures[key];
      if (
        measureValue === null ||
        measureValue === undefined ||
        Number.isNaN(Number(measureValue))
      ) {
        return [];
      }
      return [Number(measureValue)];
    });

    if (values.length > 0) {
      const sum = values.reduce((a, b) => a + b, 0);
      rollupDetails[key] = {
        values,
        min: Math.min(...values),
        max: Math.max(...values),
        count: values.length,
        avg: Math.round(sum / values.length),
        sum,
      };
    }
  });
  return rollupDetails;
}

function buildRowHierarchy(
  data: any[],
  config: PivotTableConfig,
  columnKeys: string[]
): PivotRow[] {
  // Step 1: Group data by each row level
  function groupDataByLevel(rows: any[], depth: number): Map<string, any[]> {
    const groups = new Map<string, any[]>();
    const currentGroup = config.rowGroups[depth];

    // If this is the measures level, create a group for each measure
    if (
      currentGroup.target === "measure" &&
      currentGroup.attribute === "name"
    ) {
      config.aggregations.forEach((agg) => {
        groups.set(agg.title || agg.field.split(".")[1], rows);
      });
      return groups;
    }

    rows.forEach((row) => {
      const value = row[`${currentGroup.target}.${currentGroup.attribute}`];
      if (value === null || value === undefined || value === "__VZBL::null__")
        return;

      const displayValue = getDisplayValue(value);
      const existing = groups.get(displayValue) || [];
      groups.set(displayValue, [...existing, row]);
    });

    return groups;
  }

  // Step 2: Build hierarchy recursively
  function buildLevel(
    rows: any[],
    depth: number,
    parentKey: string = "",
    parentMeasure?: string
  ): PivotRow[] {
    if (depth >= config.rowGroups.length) return [];

    const groups = groupDataByLevel(rows, depth);
    const result: PivotRow[] = [];
    const currentGroup = config.rowGroups[depth];
    const isMeasureRow =
      currentGroup.target === "measure" && currentGroup.attribute === "name";

    for (const [displayValue, groupRows] of groups) {
      const currentKey = parentKey
        ? `${parentKey}-${displayValue}`
        : displayValue;
      const children = buildLevel(
        groupRows,
        depth + 1,
        currentKey,
        isMeasureRow ? displayValue : parentMeasure
      );

      let measures: Record<string, ComparisonValue | null> = {};
      let rowField = currentGroup;
      // For measure rows, only show the value for this specific measure
      if (isMeasureRow) {
        const aggregation = config.aggregations.find(
          (agg) => (agg.title || agg.field.split(".")[1]) === displayValue
        );
        if (aggregation) {
          // Extract target and attribute from the aggregation field
          const [target, attribute] = aggregation.field.split(".");
          // Update the field property with proper structure
          rowField = {
            ...currentGroup,
            target,
            attribute,
            operator: aggregation.reducer,
            measureKey: aggregation.key,
          };
        }
        //if row is measure then column is pivot, else column is measure
        if (aggregation && config.columnGroups.length > 0) {
          columnKeys.forEach((columnKey) => {
            const matchingRows = filterByColumnKey(
              groupRows,
              columnKey,
              config.columnGroups
            );
            const columnMeasures = aggregateMeasures(matchingRows, [
              aggregation,
            ]);
            // Ensure we're getting the parsed comparison value
            const rawValue = columnMeasures[aggregation.key];
            measures[columnKey] = rawValue;
          });
        } else if (aggregation) {
          // When no column groups, process the measure directly
          const measureValue = aggregateMeasures(groupRows, [aggregation]);
          measures = measureValue;
        }
      } else if (config.columnGroups.length > 0) {
        columnKeys.forEach((columnKey) => {
          const matchingRows = filterByColumnKey(
            groupRows,
            columnKey,
            config.columnGroups
          );

          const aggregated = aggregateMeasures(
            matchingRows,
            config.aggregations
          );
          measures[columnKey] = aggregated[Object.keys(aggregated)[0]];
        });
      } else {
        // For pivot rows with measure columns
        measures = aggregateMeasures(groupRows, config.aggregations);
      }

      const row: PivotRow = {
        key: currentKey,
        value: displayValue,
        measures,
        depth,
        isExpandable: children.length > 0,
        children: children.length > 0 ? children : undefined,
        groupBys: config.rowGroups,
        measureName: isMeasureRow ? displayValue : undefined,
        field: rowField,
        rollupDetails: computeRollupDetails(
          children,
          Object.keys(measures),
          config.rollupMeasureKey
        ),
      };

      result.push(row);
    }

    return result.sort((a, b) => {
      const aValue = getDisplayValue(a.value);
      const bValue = getDisplayValue(b.value);
      return aValue.localeCompare(bValue);
    });
  }

  return buildLevel(data, 0);
}

export function usePivotTableData(
  rawData: any[],
  config: PivotTableConfig
): UsePivotTableResult {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const columnKeys = useMemo(
    () =>
      config.columnGroups.length > 0
        ? generateColumnKeys(rawData, config.columnGroups)
        : [],
    [rawData, config.columnGroups]
  );

  const processedData = useMemo(
    () => buildRowHierarchy(rawData, config, columnKeys),
    [rawData, config, columnKeys]
  );

  const toggleExpanded = useCallback((key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);
  return {
    processedData,
    expandedRows,
    toggleExpanded,
    columnKeys,
  };
}
